import { ApiService } from '../services/api';
import { Store } from '../state/store';
import type { Model, Provider } from '../state/types';
import { Component } from './base';

export class SettingsComponent extends Component {
    private store: Store;
    private api: ApiService;
    private activeTab: 'models' | 'providers' | 'general' = 'models';
    private editingModelId: string | null = null;
    private editingProviderId: string | null = null;
    private apiKeyVisible = false;
    private modal: HTMLElement | null = null;

    constructor(store: Store, api: ApiService) {
        super(document.createElement('div'));
        this.store = store;
        this.api = api;
    }

    show(): void {
        this.render();
        document.body.appendChild(this.element);
        document.body.style.overflow = 'hidden';
        this.loadData();
    }

    hide(): void {
        this.element.remove();
        document.body.style.overflow = '';
    }

    private async loadData(): Promise<void> {
        try {
            const [models, providers, settings] = await Promise.all([
                this.api.listModels(),
                this.api.listProviders(),
                this.api.getSettings(),
            ]);
            this.store.setModels(models);
            this.store.setProviders(providers);
            this.store.setDefaultModel(settings.default_model_id);
            this.render();
        } catch (error) {
            console.error('Failed to load settings:', error);
        }
    }

    protected render(): void {
        this.element.className = 'settings-modal-overlay';
        this.element.innerHTML = `
            <div class="settings-modal">
                <div class="settings-modal-header">
                    <h2>Settings</h2>
                    <button id="settings-close" class="settings-close" aria-label="Close settings">
                        <i data-lucide="x"></i>
                    </button>
                </div>
                <div class="settings-tabs">
                    <button class="settings-tab ${this.activeTab === 'models' ? 'active' : ''}" data-tab="models">Models</button>
                    <button class="settings-tab ${this.activeTab === 'providers' ? 'active' : ''}" data-tab="providers">Providers</button>
                    <button class="settings-tab ${this.activeTab === 'general' ? 'active' : ''}" data-tab="general">General</button>
                </div>
                <div class="settings-modal-body">
                    ${this.renderTabContent()}
                </div>
            </div>
        `;

        (window as any).lucide?.createIcons();
        this.bindEvents();
    }

    private renderTabContent(): string {
        switch (this.activeTab) {
            case 'models':
                return this.renderModelsTab();
            case 'providers':
                return this.renderProvidersTab();
            case 'general':
                return this.renderGeneralTab();
        }
    }

    private renderModelsTab(): string {
        const models = this.store.getModels();
        const providers = this.store.getProviders();
        const defaultModel = this.store.getDefaultModel();

        if (this.editingModelId === 'new') {
            return this.renderModelForm(null, providers);
        }
        if (this.editingModelId) {
            const model = models.find(m => m.id === this.editingModelId) || null;
            return this.renderModelForm(model, providers);
        }

        return `
            <div class="settings-section">
                <div class="settings-section-header">
                    <h3>Models</h3>
                    <button id="add-model-btn" class="settings-add-btn">Add Model</button>
                </div>
                <div class="settings-list">
                    ${models.map(model => {
                        const provider = providers.find(p => p.id === model.provider_id);
                        const isDefault = model.id === defaultModel;
                        return `
                            <div class="settings-list-item">
                                <div class="settings-list-item-info">
                                    <div class="settings-list-item-title">
                                        ${model.display_name}
                                        ${isDefault ? '<span class="settings-badge">Default</span>' : ''}
                                        ${model.vision_capable ? '<span class="settings-badge vision"><i data-lucide="eye" class="settings-vision-icon"></i>Vision</span>' : ''}
                                    </div>
                                    <div class="settings-list-item-subtitle">${provider?.name || model.provider_id} · ${model.id}</div>
                                </div>
                                <div class="settings-list-item-actions">
                                    ${!isDefault ? `<button class="settings-action-btn set-default" data-id="${model.id}">Set Default</button>` : ''}
                                    <button class="settings-action-btn edit" data-id="${model.id}">Edit</button>
                                    <button class="settings-action-btn delete" data-id="${model.id}" ${models.length <= 1 ? 'disabled' : ''}>Delete</button>
                                </div>
                            </div>
                        `;
                    }).join('')}
                </div>
            </div>
        `;
    }

    private renderModelForm(model: Model | null, providers: Provider[]): string {
        const isNew = model === null;
        const params = model?.parameters;
        const providerOptions = providers.map(p =>
            `<option value="${p.id}" ${model?.provider_id === p.id ? 'selected' : ''}>${p.name}</option>`
        ).join('');

        return `
            <div class="settings-section">
                <h3>${isNew ? 'Add Model' : 'Edit Model'}</h3>
                <form id="model-form" class="settings-form">
                    <div class="settings-form-group">
                        <label for="model-id">Model ID</label>
                        <input type="text" id="model-id" name="id" ${isNew ? '' : 'disabled'} value="${model?.id || ''}" placeholder="accounts/provider/models/model-id" required>
                        ${isNew ? '' : '<input type="hidden" name="id" value="' + model?.id + '">'}
                    </div>
                    <div class="settings-form-group">
                        <label for="model-display-name">Display Name</label>
                        <input type="text" id="model-display-name" name="display_name" value="${model?.display_name || ''}" required>
                    </div>
                    <div class="settings-form-group">
                        <label for="model-provider">Provider</label>
                        <select id="model-provider" name="provider_id" required>
                            <option value="">Select a provider</option>
                            ${providerOptions}
                        </select>
                        ${providers.length === 0 ? '<p class="settings-form-hint">No providers available. Add a provider first.</p>' : ''}
                    </div>
                    <div class="settings-form-row">
                        <div class="settings-form-group">
                            <label for="model-temperature">Temperature</label>
                            <input type="number" id="model-temperature" name="temperature" step="0.1" min="0" max="2" value="${params?.temperature ?? 0.6}" required>
                        </div>
                        <div class="settings-form-group">
                            <label for="model-max-tokens">Max Tokens</label>
                            <input type="number" id="model-max-tokens" name="max_tokens" min="1" value="${params?.max_tokens ?? 4096}" required>
                        </div>
                    </div>
                    <div class="settings-form-group checkbox">
                        <label>
                            <input type="checkbox" name="vision_capable" ${model?.vision_capable ? 'checked' : ''}>
                            Vision capable
                        </label>
                    </div>
                    <div class="settings-form-group">
                        <label for="model-system-prompt">System Prompt (optional)</label>
                        <textarea id="model-system-prompt" name="system_prompt" rows="4" placeholder="Leave empty to use the general system prompt">${model?.system_prompt || ''}</textarea>
                    </div>
                    <div class="settings-form-actions">
                        <button type="button" id="model-cancel" class="settings-btn secondary">Cancel</button>
                        <button type="submit" class="settings-btn primary">${isNew ? 'Add' : 'Save'}</button>
                    </div>
                </form>
            </div>
        `;
    }

    private renderProvidersTab(): string {
        const providers = this.store.getProviders();
        const models = this.store.getModels();

        if (this.editingProviderId === 'new') {
            return this.renderProviderForm(null);
        }
        if (this.editingProviderId) {
            const provider = providers.find(p => p.id === this.editingProviderId) || null;
            return this.renderProviderForm(provider);
        }

        return `
            <div class="settings-section">
                <div class="settings-section-header">
                    <h3>Providers</h3>
                    <button id="add-provider-btn" class="settings-add-btn">Add Provider</button>
                </div>
                <div class="settings-list">
                    ${providers.map(provider => {
                        const inUse = models.some(m => m.provider_id === provider.id);
                        return `
                            <div class="settings-list-item">
                                <div class="settings-list-item-info">
                                    <div class="settings-list-item-title">${provider.name}</div>
                                    <div class="settings-list-item-subtitle">${provider.endpoint_url}</div>
                                </div>
                                <div class="settings-list-item-actions">
                                    <button class="settings-action-btn edit" data-id="${provider.id}">Edit</button>
                                    <button class="settings-action-btn delete" data-id="${provider.id}" ${inUse ? 'disabled' : ''}>Delete</button>
                                </div>
                            </div>
                        `;
                    }).join('')}
                </div>
            </div>
        `;
    }

    private renderProviderForm(provider: Provider | null): string {
        const isNew = provider === null;
        return `
            <div class="settings-section">
                <h3>${isNew ? 'Add Provider' : 'Edit Provider'}</h3>
                <form id="provider-form" class="settings-form">
                    <div class="settings-form-group">
                        <label for="provider-name">Name</label>
                        <input type="text" id="provider-name" name="name" value="${provider?.name || ''}" required>
                    </div>
                    <div class="settings-form-group">
                        <label for="provider-endpoint">Endpoint URL</label>
                        <input type="url" id="provider-endpoint" name="endpoint_url" value="${provider?.endpoint_url || ''}" placeholder="https://api.example.com/v1/chat/completions" required>
                    </div>
                    <div class="settings-form-group">
                        <label for="provider-api-key">API Key</label>
                        <div class="settings-api-key-input">
                            <input type="${this.apiKeyVisible ? 'text' : 'password'}" id="provider-api-key" name="api_key" value="${provider?.api_key || ''}" placeholder="${isNew ? '' : 'Leave empty to keep existing key'}" ${isNew ? 'required' : ''}>
                            <button type="button" id="toggle-api-key" class="settings-icon-btn" aria-label="Toggle API key visibility">
                                <i data-lucide="${this.apiKeyVisible ? 'eye-off' : 'eye'}"></i>
                            </button>
                        </div>
                    </div>
                    <div class="settings-form-actions">
                        <button type="button" id="provider-cancel" class="settings-btn secondary">Cancel</button>
                        <button type="submit" class="settings-btn primary">${isNew ? 'Add' : 'Save'}</button>
                    </div>
                </form>
            </div>
        `;
    }

    private renderGeneralTab(): string {
        const models = this.store.getModels();
        const defaultModel = this.store.getDefaultModel();
        const options = models.map(m =>
            `<option value="${m.id}" ${m.id === defaultModel ? 'selected' : ''}>${m.display_name}</option>`
        ).join('');

        return `
            <div class="settings-section">
                <h3>General</h3>
                <form id="general-form" class="settings-form">
                    <div class="settings-form-group">
                        <label for="default-model">Default Model</label>
                        <select id="default-model" name="default_model_id" required>
                            ${options}
                        </select>
                    </div>
                    <div class="settings-form-actions">
                        <button type="submit" class="settings-btn primary">Save</button>
                    </div>
                </form>
            </div>
        `;
    }

    private bindEvents(): void {
        this.element.querySelector('#settings-close')?.addEventListener('click', () => this.hide());
        this.element.addEventListener('click', (e) => {
            if (e.target === this.element) this.hide();
        });

        this.element.querySelectorAll('.settings-tab').forEach(tab => {
            tab.addEventListener('click', (e) => {
                const target = e.currentTarget as HTMLElement;
                this.activeTab = target.dataset.tab as 'models' | 'providers' | 'general';
                this.editingModelId = null;
                this.editingProviderId = null;
                this.apiKeyVisible = false;
                this.render();
            });
        });

        this.element.querySelector('#add-model-btn')?.addEventListener('click', () => {
            this.editingModelId = 'new';
            this.render();
        });

        this.element.querySelector('#add-provider-btn')?.addEventListener('click', () => {
            this.editingProviderId = 'new';
            this.apiKeyVisible = false;
            this.render();
        });

        this.element.querySelectorAll('.settings-action-btn.edit').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const target = e.currentTarget as HTMLElement;
                const id = target.dataset.id;
                if (!id) return;
                if (this.activeTab === 'models') {
                    this.editingModelId = id;
                } else {
                    this.editingProviderId = id;
                    this.apiKeyVisible = false;
                }
                this.render();
            });
        });

        this.element.querySelectorAll('.settings-action-btn.delete').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const target = e.currentTarget as HTMLElement;
                const id = target.dataset.id;
                if (!id) return;
                if (this.activeTab === 'models') {
                    await this.handleDeleteModel(id);
                } else {
                    await this.handleDeleteProvider(id);
                }
            });
        });

        this.element.querySelectorAll('.settings-action-btn.set-default').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const target = e.currentTarget as HTMLElement;
                const id = target.dataset.id;
                if (!id) return;
                await this.handleSetDefaultModel(id);
            });
        });

        this.element.querySelector('#model-cancel')?.addEventListener('click', () => {
            this.editingModelId = null;
            this.render();
        });

        this.element.querySelector('#provider-cancel')?.addEventListener('click', () => {
            this.editingProviderId = null;
            this.apiKeyVisible = false;
            this.render();
        });

        this.element.querySelector('#toggle-api-key')?.addEventListener('click', () => {
            this.apiKeyVisible = !this.apiKeyVisible;
            this.render();
        });

        this.element.querySelector('#model-form')?.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleModelSubmit(e.currentTarget as HTMLFormElement);
        });

        this.element.querySelector('#provider-form')?.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleProviderSubmit(e.currentTarget as HTMLFormElement);
        });

        this.element.querySelector('#general-form')?.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleGeneralSubmit(e.currentTarget as HTMLFormElement);
        });
    }

    private async handleModelSubmit(form: HTMLFormElement): Promise<void> {
        const formData = new FormData(form);
        const id = formData.get('id') as string;
        const display_name = formData.get('display_name') as string;
        const provider_id = formData.get('provider_id') as string;
        const temperature = parseFloat(formData.get('temperature') as string);
        const max_tokens = parseInt(formData.get('max_tokens') as string, 10);
        const vision_capable = formData.get('vision_capable') === 'on';
        const system_prompt = (formData.get('system_prompt') as string) || null;

        if (!id || !display_name || !provider_id) return;

        try {
            if (this.editingModelId === 'new') {
                await this.api.createModel({
                    id,
                    display_name,
                    provider_id,
                    temperature,
                    max_tokens,
                    vision_capable,
                    system_prompt,
                });
            } else {
                await this.api.updateModel(id, {
                    display_name,
                    provider_id,
                    temperature,
                    max_tokens,
                    vision_capable,
                    system_prompt,
                });
            }
            this.editingModelId = null;
            await this.loadData();
        } catch (error) {
            console.error('Failed to save model:', error);
            alert('Failed to save model. See console for details.');
        }
    }

    private async handleProviderSubmit(form: HTMLFormElement): Promise<void> {
        const formData = new FormData(form);
        const name = formData.get('name') as string;
        const endpoint_url = formData.get('endpoint_url') as string;
        const api_key = formData.get('api_key') as string;

        if (!name || !endpoint_url) return;

        try {
            if (this.editingProviderId === 'new') {
                if (!api_key) return;
                await this.api.createProvider({ name, endpoint_url, api_key });
            } else if (this.editingProviderId) {
                const updates: Partial<Provider> = { name, endpoint_url };
                if (api_key) updates.api_key = api_key;
                await this.api.updateProvider(this.editingProviderId, updates);
            }
            this.editingProviderId = null;
            this.apiKeyVisible = false;
            await this.loadData();
        } catch (error) {
            console.error('Failed to save provider:', error);
            alert('Failed to save provider. See console for details.');
        }
    }

    private async handleGeneralSubmit(form: HTMLFormElement): Promise<void> {
        const formData = new FormData(form);
        const default_model_id = formData.get('default_model_id') as string;
        if (!default_model_id) return;

        try {
            await this.api.updateSettings({ default_model_id });
            this.store.setDefaultModel(default_model_id);
        } catch (error) {
            console.error('Failed to update default model:', error);
            alert('Failed to update default model. See console for details.');
        }
    }

    private async handleSetDefaultModel(id: string): Promise<void> {
        try {
            await this.api.updateSettings({ default_model_id: id });
            this.store.setDefaultModel(id);
            await this.loadData();
        } catch (error) {
            console.error('Failed to set default model:', error);
            alert('Failed to set default model. See console for details.');
        }
    }

    private async handleDeleteModel(id: string): Promise<void> {
        const models = this.store.getModels();
        if (models.length <= 1) {
            alert('Cannot delete the last model.');
            return;
        }
        if (!confirm('Are you sure you want to delete this model?')) return;

        try {
            await this.api.deleteModel(id);
            const selectedModel = this.store.getSelectedModel();
            if (selectedModel === id) {
                const defaultModel = this.store.getDefaultModel();
                const fallback = models.find(m => m.id !== id)?.id || defaultModel;
                this.store.setSelectedModel(fallback);
            }
            await this.loadData();
        } catch (error) {
            console.error('Failed to delete model:', error);
            alert('Failed to delete model. See console for details.');
        }
    }

    private async handleDeleteProvider(id: string): Promise<void> {
        const models = this.store.getModels();
        if (models.some(m => m.provider_id === id)) {
            alert('Cannot delete a provider that is in use by a model.');
            return;
        }
        if (!confirm('Are you sure you want to delete this provider?')) return;

        try {
            await this.api.deleteProvider(id);
            await this.loadData();
        } catch (error) {
            console.error('Failed to delete provider:', error);
            alert('Failed to delete provider. See console for details.');
        }
    }
}
